import type { FValues, Gradients } from ".";
import { GRAVITY_FT_SEC } from "../../constants";
import type { PipeFrictionContext } from "../d";

export function calculateGradientsBubble(
  context: PipeFrictionContext,
  fValues: FValues
): Gradients {
  const {
    fluidPropertiesContext: {
      fluidProperties: {
        gasDensityLBMFT3,
        liquidSuperficialVelocityFTSEC,
        liquidDensityLBMFT3,
        gasLiquidRatioSCFSTB
      },
      fluidFrictionProperties: {
        liquidVelocityNumber,
        liquidInterfacialTension,
        pipeDiameterNumber,
        liquidReynoldsNumber,
        gasVelocityNumber
      }
    },
    VSG,
    epsilonD
  } = context;
  const { F1, F2, F3tick } = fValues;

  // D241: =D202+D204*D176+D207*(D174/(1+D176))^2
  const slipCorrelation =
    F1 +
    F2 * liquidVelocityNumber +
    F3tick * (gasVelocityNumber / (1 + liquidVelocityNumber)) ** 2;

  // CHEN FRICTION
  // D243: =(1/(-2*LOG10(D305/3.7065-(5.0452/D303)*LOG10(((D305^1.1098)/2.8257)+(5.8506/(D303^0.8981))))))^2
  const f1 =
    (1 /
      (-2 *
        Math.log10(
          epsilonD / 3.7065 -
            (5.0452 / liquidReynoldsNumber) *
              Math.log10(
                epsilonD ** 1.1098 / 2.8257 +
                  5.8506 / liquidReynoldsNumber ** 0.8981
              )
        ))) **
    2;

  // D245: =D243*D42*D178^(2/3)
  const f2 = f1 * gasLiquidRatioSCFSTB * pipeDiameterNumber ** (2 / 3);

  // D247 =1+D243*SQRT(D42/50)
  const f3 = 1 + f1 * Math.sqrt(gasLiquidRatioSCFSTB / 50);

  // D249: =D243*(D245/D247)
  const fw = f1 * (f2 / f3);

  // D251: =2*D249*(D176*(D176+D174))/D178
  const frictionGradient =
    (2 *
      fw *
      (liquidVelocityNumber * (liquidVelocityNumber + gasVelocityNumber))) /
    pipeDiameterNumber;

  // D253: =D241/(D63/(D166*D67))^0.25
  const slipVelocity =
    slipCorrelation /
    (liquidDensityLBMFT3 / (liquidInterfacialTension * GRAVITY_FT_SEC)) ** 0.25;

  // D255: =(D253-D307-D150+SQRT((D253-D307-D150)^2+4*D253*D150))/(2*D253)
  const liquidHoldup =
    (slipVelocity -
      VSG -
      liquidSuperficialVelocityFTSEC +
      Math.sqrt(
        (slipVelocity - VSG - liquidSuperficialVelocityFTSEC) ** 2 +
          4 * slipVelocity * liquidSuperficialVelocityFTSEC
      )) /
    (2 * slipVelocity);

  // D257 =(D255*D63+(1-D255)*D62)/(144)
  const staticGradient =
    (liquidHoldup * liquidDensityLBMFT3 +
      (1 - liquidHoldup) * gasDensityLBMFT3) /
    144;

  return {
    slipCorrelation,
    f1,
    f2,
    f3,
    fw,
    frictionGradient,
    slipVelocity,
    liquidHoldup,
    staticGradient
  };
}
